import apiClient from './apiClient';

/**
 * Video Call Service
 * 
 * This service handles video call related API calls including booking access validation,
 * session management, and participant operations.
 */

// Helper function to get the base URL for video call APIs
const getVideoCallPath = (path) => `/video-calls${path}`;

/**
 * Check if the current user has access to a specific booking for video calls
 * 
 * @param {number} bookingId - The booking ID to check access for
 * @returns {Promise<Object>} Access validation result
 */
export const checkBookingAccess = async (bookingId) => {
  try {
    const response = await apiClient.get(getVideoCallPath(`/bookings/${bookingId}/access-check`));
    return {
      success: true,
      data: response.data
    };
  } catch (error) {
    console.error('❌ Error checking booking access:', error);
    
    if (error.response?.status === 401) {
      return {
        success: false,
        error: 'Authentication required',
        shouldRedirectToLogin: true
      };
    }
    
    if (error.response?.status === 403) {
      return {
        success: false,
        error: error.response.data?.message || 'Access denied',
        errorCode: error.response.data?.error_code,
        redirectTo: error.response.data?.redirect_to
      };
    }
    
    return {
      success: false,
      error: error.response?.data?.message || 'Failed to validate booking access'
    };
  }
};

/**
 * Validate booking access and handle common error scenarios
 * This is a higher-level function that includes navigation logic
 * 
 * @param {number} bookingId - The booking ID to validate
 * @param {Object} navigate - React Router navigate function
 * @param {Function} setError - Function to set error state
 * @param {Function} setLoading - Function to set loading state
 * @param {Function} onSuccess - Callback function called on successful validation
 * @returns {Promise<void>}
 */
export const validateBookingAccessWithNavigation = async (
  bookingId, 
  navigate, 
  setError, 
  setLoading, 
  onSuccess
) => {
  try {
    setLoading(true);
    setError(null);
    
    if (!bookingId) {
      throw new Error('No booking ID provided');
    }
    
    const result = await checkBookingAccess(bookingId);
    
    if (!result.success) {
      if (result.shouldRedirectToLogin) {
        navigate('/auth/login');
        return;
      }
      
      const errorMessage = result.error || 'Access denied';
      setError(errorMessage);
      
      // Redirect to appropriate page after showing error
      const redirectTo = result.redirectTo || '/auth/login';
      setTimeout(() => {
        navigate(redirectTo);
      }, 3000);
      
      return;
    }
    
    const { data } = result;
    
    if (!data.access_granted) {
      const errorMessage = data.error_details?.message || 'You do not have access to this appointment';
      const redirectTo = data.error_details?.redirect_to || '/auth/login';
      
      setError(errorMessage);
      
      setTimeout(() => {
        navigate(redirectTo);
      }, 3000);
      
      return;
    }
    
    // Access granted - call success callback with booking data
    if (onSuccess) {
      onSuccess({
        participantType: data.participant_type,
        bookingInfo: data.booking_info,
        bookingId: data.booking_id
      });
    }
    
  } catch (error) {
    console.error('❌ Booking validation failed:', error);
    setError(error.message || 'Failed to validate appointment access');
    
    setTimeout(() => {
      navigate('/auth/login');
    }, 3000);
  } finally {
    setLoading(false);
  }
};

/**
 * Get user's home dashboard path based on their roles
 * 
 * @param {Object} currentUser - Current user object
 * @returns {string} Dashboard path
 */
export const getUserHomePath = (currentUser) => {
  if (!currentUser) {
    return '/auth/login';
  }
  
  if (currentUser.is_superuser) {
    return '/admin/dashboard';
  } else if (currentUser.is_doctor) {
    return '/doctor/dashboard';
  } else if (currentUser.is_clinician) {
    return '/clinician/dashboard';
  } else if (currentUser.is_patient) {
    return '/patient/dashboard';
  } else {
    return '/auth/role-selection';
  }
};

/**
 * Create or get video session for a booking
 * 
 * @param {number} bookingId - The booking ID
 * @returns {Promise<Object>} Session creation result
 */
export const createOrGetVideoSession = async (bookingId) => {
  try {
    const response = await apiClient.post(getVideoCallPath(`/sessions/create-or-get/${bookingId}`));
    return {
      success: true,
      data: response.data
    };
  } catch (error) {
    console.error('❌ Error creating/getting video session:', error);
    return {
      success: false,
      error: error.response?.data?.detail || 'Failed to create video session'
    };
  }
};

/**
 * Join a video session
 * 
 * @param {number} bookingId - The booking ID
 * @param {Object} joinRequest - Join request data
 * @returns {Promise<Object>} Join result
 */
export const joinVideoSession = async (bookingId, joinRequest) => {
  try {
    const response = await apiClient.post(getVideoCallPath(`/sessions/${bookingId}/join`), joinRequest);
    return {
      success: true,
      data: response.data
    };
  } catch (error) {
    console.error('❌ Error joining video session:', error);
    return {
      success: false,
      error: error.response?.data?.detail || 'Failed to join video session'
    };
  }
};

/**
 * Leave a video session
 * 
 * @param {number} bookingId - The booking ID
 * @param {Object} leaveRequest - Leave request data
 * @returns {Promise<Object>} Leave result
 */
export const leaveVideoSession = async (bookingId, leaveRequest) => {
  try {
    const response = await apiClient.delete(getVideoCallPath(`/sessions/${bookingId}/leave`), {
      data: leaveRequest
    });
    return {
      success: true,
      data: response.data
    };
  } catch (error) {
    console.error('❌ Error leaving video session:', error);
    return {
      success: false,
      error: error.response?.data?.detail || 'Failed to leave video session'
    };
  }
};

/**
 * Get session clinician info
 * 
 * @param {number} bookingId - The booking ID
 * @returns {Promise<Object>} Clinician info result
 */
export const getSessionClinicianInfo = async (bookingId) => {
  try {
    const response = await apiClient.get(getVideoCallPath(`/sessions/${bookingId}/clinician-info`));
    return {
      success: true,
      data: response.data
    };
  } catch (error) {
    console.error('❌ Error getting clinician info:', error);
    return {
      success: false,
      error: error.response?.data?.detail || 'Failed to get clinician info'
    };
  }
};

export default {
  checkBookingAccess,
  validateBookingAccessWithNavigation,
  getUserHomePath,
  createOrGetVideoSession,
  joinVideoSession,
  leaveVideoSession,
  getSessionClinicianInfo
};










